import { Box } from "@mui/material";
import strings from "global/constants/StringConstants";
import { globalEmitter } from "utils/emitter";
import {
  DateRange,
  Error,
  FormattedResources,
  Resources,
} from "models/interfaces";
import { logOutAction } from "redux/authSlice";
import { store } from "utils/store";
import history from "utils/history";
import MomentHelpers from "helpers/MomentHelpers";
import moment, { DurationInputArg2 } from "moment-timezone";
import { PhoneNumberUtil } from "google-libphonenumber";
import viewpaths from "global/constants/ViewPathConstants";
import UAParser from "ua-parser-js";
import notifiers from "global/constants/NotificationConstants";

const methodsContext = this;

export const isTruthy = (value: any, shouldCheckByType: boolean = true) => {
  const validatedByType = shouldCheckByType
    ? customValidatorByType(value)
    : true;

  if (value !== null && value !== undefined && validatedByType) {
    return true;
  }
  return false;
};

const customValidatorByType = (value: any) => {
  if (value !== undefined && value !== null) {
    const type = typeof value;
    switch (type) {
      case "string":
        return value.trim() !== "";
      case "object":
        if (Array.isArray(value)) {
          return value.length > 0;
        } else {
          return Object.keys(value).length > 0;
        }
      default:
        return true;
    }
  }
};

export const compareStrings = (string1: string, string2: string) => {
  if (!(isTruthy(string1) || isTruthy(string2))) {
    return true;
  } else {
    if (string1 && string2) {
      if (string1.toLowerCase() === string2.toLowerCase()) {
        return true;
      }
    }
  }
  return false;
};

export const getGreetings = () => {
  const date = new Date();
  let hours = date.getHours();
  let status =
    hours < 12
      ? "Good Morning"
      : hours <= 18 && hours >= 12
      ? "Good Afternoon"
      : "Good Evening";

  return status;
};

export const openInfoNotification = (message: any, title: string = "Info") => {
  globalEmitter.emit(strings.notification, {
    type: strings.info,
    message: message,
    title: title,
  });
};

export const openSuccessNotification = (
  message: any,
  title: string = "Success"
) => {
  globalEmitter.emit(strings.notification, {
    type: strings.success,
    message: message,
    title: title,
  });
};

export const openWarningNotification = (
  message: any,
  title: string = "Warning"
) => {
  globalEmitter.emit(strings.notification, {
    type: strings.warning,
    message: message,
    title: title,
  });
};

export const handleErrorMessage = (error: Error) => {
  if (error.statusCode || error.status || error.code) {
    switch (error.statusCode || error.status || error.code) {
      case 400:
        return openErrorNotification("Please check your request and try again");
      case 401:
        store.dispatch(logOutAction({}));
        history.push(viewpaths.landingViewPath);
        return openErrorNotification(notifiers.LOGGEDOUT);
      case 403:
        return openErrorNotification(
          "You are not authorized to access this resource."
        );
      case 404:
        return openErrorNotification(
          "The requested resource could not be found."
        );
      case 429:
        return openErrorNotification(
          "Our servers are currently experiencing a high volume of requests. Please try again in a few minutes."
        );
      case 500:
        return openErrorNotification(
          "There was an error on the server side. Please try again later."
        );
      case 406:
        return openErrorNotification(error.message);
      default:
        return openErrorNotification(
          "There was an error on the server side. Please try again later."
        );
    }
  } else if (
    error.errorMessage === "Failed to fetch" &&
    error.name === "TypeError"
  ) {
    return openErrorNotification(
      "This is a critical error that requires immediate attention from the support team. Please contact them."
    );
  }
};

export const openErrorNotification = (
  message: any,
  title: string = "Error"
) => {
  globalEmitter.emit(strings.notification, {
    type: strings.error,
    message: message,
    title: title,
  });
};

export function debounce(func: Function, wait: number) {
  let timeout: any;
  return function (...args: any) {
    const context = methodsContext;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export function debounceEventHandler(func: Function, wait: number) {
  const debounced = debounce(func, wait);
  return function (event: any) {
    event.persist();
    return debounced(event);
  };
}

export const getFormattedNumbers = (value: string) => {
  const matches = value.match(/\d+/g);
  let number = "";
  if (matches !== null) {
    matches.forEach((match) => {
      number += match;
    });
  }
  if (number.length === 10) {
    value = number.replace(/^(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  }
  return { number: number, maskedNumber: value };
};

export const getFileExtension = (fileName: string) => {
  const ext = /^.+\.([^.]+)$/.exec(fileName);
  return ext === null ? "" : ext[1];
};

export const validatePhoneNumber = (mobile: string) => {
  var re = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
  return re.test(mobile);
};

export const isPhoneValid = (phone: string) => {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error: any) {
    return false;
  }
};

export const tableRowRightAlign = (value: any) => {
  return (
    <Box component="span" sx={{ textAlign: "right", display: "block" }}>
      {value}
    </Box>
  );
};

const doesResourcesExistInLocalStorage = (resources: Resources[]) => {
  if (!isTruthy(resources)) {
    store.dispatch(logOutAction({}));
    history.push(viewpaths.landingViewPath);
    return false;
  }
  return true;
};

export const convertResourceToObjectFormat = (resources: Resources[]) => {
  if (!doesResourcesExistInLocalStorage(resources)) {
    return {} as FormattedResources;
  }
  let returnObject = {} as { [key: string]: string[] };
  resources.forEach((resource) => {
    returnObject[resource.path] = resource.permissions;
  });
  return returnObject;
};

export const convertESTtoUserLocalTime = (
  inputTime: string,
  inputTimeFormat = "MM-DD-YYYY HH:mm:ss",
  outputTimeFormat = "MMMM D, YYYY h:mm A"
) => {
  const userTimeZone = MomentHelpers.guessTheTimeZone();
  const inputTimezone = "America/New_York";
  return moment
    .tz(inputTime, inputTimeFormat, inputTimezone)
    .tz(userTimeZone)
    .format(outputTimeFormat);
};

export const getABResultAfterType = (
  resultsAfterInterval: number,
  resultsAfterType: string
) => {
  if (resultsAfterInterval === 1 && resultsAfterType === "hours") {
    return "1 hour";
  } else if (resultsAfterInterval === 1 && resultsAfterType === "days") {
    return "1 day";
  } else {
    return `${resultsAfterInterval} ${resultsAfterType.toLowerCase()}`;
  }
};

export const getTentativeResultTime = (
  resultTentativeTime: any,
  resultsAfterInterval: number,
  resultsAfterType: string
) => {
  if (!resultTentativeTime) {
    return null;
  }
  const resultTime = moment(resultTentativeTime, "MMM DD, YYYY hh:mm a");
  const type = resultsAfterType as DurationInputArg2;

  return moment(resultTime.add(resultsAfterInterval, type)).format(
    "MMM DD, YYYY hh:mm a"
  );
};

export function isValidEmail(email: string = "") {
  return strings.EmailRegex.test(email);
}

export function isPasswordStrong(password: string = "") {
  return strings.PasswordRegex.test(password);
}

export function isValidOTP(otp: string = "", digits: number = 6) {
  return otp.length === digits;
}

export function checkPasswordStrength(password: string = "") {
  let percentage = 0;
  // Check minimum length
  if (password.length >= 8) {
    percentage += 25;
  }
  // Check for at least one lowercase letter
  if (/[a-z]/.test(password)) {
    percentage += 25;
  }
  // Check for at least one number
  if (/\d/.test(password)) {
    percentage += 25;
  }
  // // Check for at least one special character
  // if (/[!@#$%^&*.?]/.test(password)) {
  //   percentage += 25;
  // }
  // Check for at least one uppercase letter
  if (/[A-Z]/.test(password)) {
    percentage += 25;
  }

  // Determine strength based on percentage
  const strength = percentage === 100 ? "Strong" : "Weak";
  return { strength, percentage };
}

export function formatDateInLongDateFormat(date: Date | string) {
  return moment(date).format("LL");
}

export function formatDateWithTimeInLongDateFormat(date: Date | string) {
  return moment(date).format("MMMM D, YYYY");
}

export const convertPriceToDollarFormat = (value: number) => {
  return `$${value.toFixed(2)}`;
};

export const getPhoneNumberWithCountryCode = (number: string) => {
  const countryCode = number.substring(1, number.indexOf(" "));
  const phoneNumber = number
    .substring(number.indexOf(" "))
    .split("")
    .filter(
      (char: string) =>
        char !== " " && char !== "(" && char !== ")" && char !== "-"
    )
    .join("");

  const formattedObject = {
    countryCode: parseInt(countryCode),
    type: "Mobile",
    value: parseInt(phoneNumber),
  };

  return formattedObject;
};

export const formattedPhoneNumber = (number: string) => {
  const formattedNumber: string = number.replace(
    /(\d{3})(\d{3})(\d{4})/,
    "($1) $2-$3"
  );
  return formattedNumber;
};

export const addHours = (timeObject: any, field?: string): string => {
  let addedMinutes = 0;

  if (!field) {
    Object.keys(timeObject).forEach((key) => {
      const fieldValue = timeObject[key];
      const [hours, minutes] = fieldValue.split(":").map(Number);
      addedMinutes += hours * 60 + minutes;
    });
  } else {
    timeObject.forEach((currObj: any) => {
      const fieldValue = currObj[field];
      const [hours, minutes] = fieldValue.split(":").map(Number);
      addedMinutes += hours * 60 + minutes;
    });
  }

  const resultHours = Math.floor(addedMinutes / 60);
  const resultMinutes = addedMinutes % 60;
  return `${resultHours.toString().padStart(2, "0")}:${resultMinutes
    .toString()
    .padStart(2, "0")}`;
};

export const addDecimalHours = (timeObject: any, field?: string): string => {
  let totalDecimalHours = 0;

  const convertToDecimalAndReplace = (time: string): number => {
    return parseFloat(time.replace(":", "."));
  };

  if (!field) {
    Object.keys(timeObject).forEach((key) => {
      const fieldValue = timeObject[key];
      totalDecimalHours += convertToDecimalAndReplace(fieldValue);
    });
  } else {
    timeObject.forEach((currObj: any) => {
      const fieldValue = currObj[field];
      totalDecimalHours += convertToDecimalAndReplace(fieldValue);
    });
  }

  const totalDecimal = totalDecimalHours.toFixed(2);
  const [integerPart, decimalPart] = totalDecimal.split(".");

  return `${integerPart.padStart(2, "0")}:${decimalPart.padStart(2, "0")}`;
};

export const stringToFile = (url: string, fileName: string): File => {
  const byteString = atob(url.split(",")[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], fileName, { type: "image/png" });
};

export const capitalizeFirstLetter = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const calculateRelativeTime = (inputTime: string): string => {
  const userTimeZone = moment.tz.guess();
  const providedDate = moment.tz(inputTime, "America/New_York"); // Assuming input time is in EST
  const localTime = providedDate.clone().tz(userTimeZone);
  return localTime.fromNow();
};

export const formatToCurrency = (value: number, currency: string = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(value);
};

export const getFormattedStatsCount = (value: number) => {
  return new Intl.NumberFormat("en-US").format(value);
};

export const trimValue = (value: string) => {
  return value.trim();
};

export const formatAddress = (address: {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  pinCode: string;
}) => {
  const addressComponents = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.country,
    address.pinCode,
  ];
  return addressComponents
    .filter((component) => isTruthy(component))
    .join(", ");
};

export const blockInvalidChar = (event: any) =>
  ["e", "E", "+", "-"].includes(event.key) && event.preventDefault();

export const isDateBetweenTwoDates = (
  date: string,
  startDate: string,
  endDate: string
) => {
  return moment(date).isBetween(
    moment(startDate),
    moment(endDate),
    undefined,
    "[]"
  );
};

export const handleWheelEvent = (event: React.WheelEvent<HTMLInputElement>) => {
  const inputElement = event.target as HTMLInputElement;
  inputElement.blur();
  setTimeout(() => inputElement.focus(), 0);
  event.preventDefault();
};

export const getUserAgentDetails = (userAgent: string) => {
  if (isTruthy(userAgent)) {
    const parser = new UAParser(userAgent);
    const result = parser.getResult();

    const browserName = result.browser.name || "Unknown Browser";
    const browserVersion = result.browser.version || "Unknown Version";

    const osName = result.os.name || "Unknown OS";

    const deviceType = result.device.type
      ? result.device.type
      : "Desktop/Laptop";

    return `${browserName} V${browserVersion} [${deviceType}, ${osName}]`;
  } else {
    return "-";
  }
};

export const ExportCSV = (headers: any, title: any, defaultEmail: string) => {
  const convertToCSV = () => {
    let str = "";
    str += getHeaderData();
    str += getDefaultRowData();
    return str;
  };

  const getHeaderData = () => {
    let str = "";
    headers.forEach((header: any) => {
      if (str !== "") str += ",";
      str += header;
    });
    str += "\r\n";
    return str;
  };

  const getDefaultRowData = () => {
    return "default@example.com,John,Doe,+1 (123) 456 7891,201-Test 123 Main St,12345,New York,New York,USA,Tag,EST\r\n";
  };

  const exportCSVFile = () => {
    let csv = convertToCSV();
    let exportedFilename = title + ".csv" || "export.csv";

    let blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    let link = document.createElement("a");
    if (link.download !== undefined) {
      let url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return exportCSVFile;
};

export default ExportCSV;

export const capitalizeEntity = (entity: string) => {
  return entity
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
};

export const copyTextHandler = async (requiredValue: string) => {
  try {
    await navigator.clipboard.writeText(requiredValue);
    openSuccessNotification("Your text has been copied successfully!");
  } catch (error: any) {
    openErrorNotification(
      isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
    );
  }
};

export const convertESTtoUserLocalDateAndTime = (
  inputTime: string,
  outputTimeFormat: string = "MMM DD, YYYY hh:mm a",
  inputTimeFormat: string = "YYYY-MM-DDTHH:mm:s"
) => {
  const userTimeZone = MomentHelpers.guessTheTimeZone();
  const inputTimezone = "America/New_York";
  return moment
    .tz(inputTime, inputTimeFormat, inputTimezone)
    .tz(userTimeZone)
    .format(outputTimeFormat);
};

export const setFileName = (fileName: string) => {
  let filenameUpdated = fileName?.split("/").pop();
  return filenameUpdated;
};

export const convertToEstTime = (
  dateTime: any,
  outputFormat: string = "MM/DD/yyyy HH:mm:ss"
) => {
  return moment(dateTime).tz("America/New_York").format(outputFormat);
};

export function formatDateInLongDateTimeFormat(
  date: Date | string,
  outputFormat: string = "MMMM D, YYYY [at] h:mm A"
) {
  const dateAndTime = moment(date, "MM/DD/YYYY HH:mm:ss");
  const formattedDate = dateAndTime.format(outputFormat);
  return formattedDate;
}

export function formateRelativeTimeFormat(date: Date | string) {
  const dateAndTime = moment(date, "MM/DD/YYYY HH:mm:ss");
  const formattedDate = dateAndTime.fromNow();
  return formattedDate;
}

export const timeZones = moment.tz.names().map((tz) => {
  const offsetMinutes = moment.tz(tz).utcOffset(); // Offset in minutes
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60); // Get the hours
  const offsetMinutesRemainder = Math.abs(offsetMinutes) % 60; // Get the remaining minutes
  const formattedOffset = `(GMT${offsetMinutes >= 0 ? "+" : "-"}${offsetHours
    .toString()
    .padStart(2, "0")}:${offsetMinutesRemainder.toString().padStart(2, "0")})`;

  return {
    label: `${formattedOffset} ${tz.replace("_", " ")}`, // Display exact offset
    value: tz,
  };
});

export const validatePageQuery = (pageFromUrl: any) => {
  if (!pageFromUrl || isNaN(+pageFromUrl) || +pageFromUrl <= 0) {
    return 1;
  }
  return +pageFromUrl;
};

export const validatePerPageQuery = (perPageFromUrl: any) => {
  if (!perPageFromUrl || isNaN(+perPageFromUrl) || +perPageFromUrl < 10) {
    return 10;
  } else if (+perPageFromUrl > 100) {
    return 100;
  } else if (+perPageFromUrl % 5 !== 0) {
    return +perPageFromUrl - (+perPageFromUrl % 5);
  }
  return +perPageFromUrl;
};

export const validateCampaignCategoryQuery = (campaignsFromUrl: any) => {
  if (
    !["All", "Draft", "Submitted", "Completed", "Failed"].includes(
      campaignsFromUrl
    )
  ) {
    return "All";
  }
  return campaignsFromUrl;
};

export const validateSocialCampaignCategoryQuery = (campaignsFromUrl: any) => {
  if (!["All", "Submitted", "Completed"].includes(campaignsFromUrl)) {
    return "All";
  }
  return campaignsFromUrl;
};

export const initialPageFilterState = (
  page: number,
  rowsPerPage: number,
  searchText: string,
  dropdownValue?: string
) => {
  return {
    page,
    rowsPerPage,
    searchText,
    dropdownValue,
  };
};

export const getDateInterval = (filter: string, dateRange: DateRange) => {
  let fromDate = dateRange.fromDate;
  let toDate = dateRange.toDate;

  switch (filter) {
    case strings.currentWeek:
      fromDate = new Date(moment().startOf("isoWeek").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.previousWeek:
      fromDate = new Date(
        moment().subtract(1, "weeks").startOf("isoWeek").format("MM-DD-yyyy")
      );
      toDate = new Date(
        moment().subtract(1, "weeks").endOf("isoWeek").format("MM-DD-yyyy")
      );
      break;
    case strings.lastSevenDays:
      fromDate = new Date(moment().subtract(7, "day").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.currentMonth:
      fromDate = new Date(moment().startOf("month").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.previousMonth:
      fromDate = new Date(
        moment().subtract(1, "month").startOf("month").format("MM-DD-yyyy")
      );
      toDate = new Date(
        moment().subtract(1, "month").endOf("month").format("MM-DD-yyyy")
      );
      break;
    case strings.lastThirtyDays:
      fromDate = new Date(moment().subtract(30, "day").format("MM-DD-yyyy"));
      toDate = new Date(moment().format("MM-DD-yyyy"));
      break;
    case strings.allTime:
      fromDate = null;
      toDate = null;
      break;
    default:
      fromDate = dateRange.fromDate;
      toDate = dateRange.toDate;
  }
  return {
    fromDate,
    toDate,
  };
};

export const last30DaysFromDate = moment()
  .subtract(30, "days")
  .format("YYYY-MM-DD");
export const last30DaysToDate = moment().format("YYYY-MM-DD");

export const getFacebookRedirectionURL = (host: string) => {
  if (host === "localhost") {
    return "https://send-uat.mailzzy.com" + viewpaths.integrationsViewPath;
  }
  const href = window.location.href;
  return href;
};

export const calculateDaysBetween = (dateRange: DateRange) => {
  if (dateRange.fromDate && dateRange.toDate) {
    return moment(dateRange.toDate).diff(moment(dateRange.fromDate), "days");
  }
  return 0;
};

export const getSocialAppPostRedirection = (socialCampaignData: any) => {
  switch (socialCampaignData.socialAppName) {
    case strings.FACEBOOK: {
      window.open(
        `https://www.facebook.com/${socialCampaignData.socialCampaign.id}`,
        "_blank"
      );
      break;
    }
    case strings.INSTAGRAM: {
      window.open(`${socialCampaignData.socialCampaign.id}`, "_blank");
      break;
    }
  }
};
